import { Circle, Clock, Loader2, Mail, Phone } from 'lucide-react';
import React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ExecutionPlanStep } from '@/services/api/agentflowService';

export interface ExecutionPlanProps {
  /** Future steps from execution plan */
  steps: ExecutionPlanStep[];
  /** Max number of items to show initially */
  maxItems?: number;
}

const getStepIcon = (stepType: string) => {
  const lowerType = stepType.toLowerCase();
  if (lowerType.includes('email')) {
    return <Mail className="h-3 w-3" />;
  }
  if (lowerType.includes('call') || lowerType.includes('voice') || lowerType.includes('dialpad') || lowerType.includes('bpo')) {
    return <Phone className="h-3 w-3" />;
  }
  return <Clock className="h-3 w-3" />;
};

const getStepTypeLabel = (stepType: string): string => {
  switch (stepType) {
    case 'voice_ai_call':
      return 'Voice AI Call';
    case 'dialpad_call':
      return 'RingCentral Call';
    case 'email_follow_up':
      return 'Email';
    case 'text_claimant':
      return 'Text Claimant';
    case 'bpo_call':
      return 'BPO Call';
    default:
      return stepType.replace(/_/g, ' ');
  }
};

export const ExecutionPlan: React.FC<ExecutionPlanProps> = ({
  steps,
  maxItems = 5,
}) => {
  const [showAll, setShowAll] = React.useState(false);

  if (!steps || steps.length === 0) {
    return (
      <div className="text-xs text-zinc-500 py-2 text-center">
        No upcoming steps
      </div>
    );
  }

  // Only show steps that are current or upcoming (not completed ones from execution plan)
  const upcomingSteps = steps.filter((s) => s.is_current || steps.indexOf(s) >= steps.findIndex((s2) => s2.is_current));
  const displaySteps = showAll ? upcomingSteps : upcomingSteps.slice(0, maxItems);
  const hasMore = upcomingSteps.length > maxItems;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-1">
        {displaySteps.map((step) => {
          const isCurrent = step.is_current;

          return (
            <div
              key={step.step_id}
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 rounded text-xs',
                isCurrent ? 'bg-violet-50' : 'bg-zinc-50'
              )}
            >
              {/* Status icon */}
              <div
                className={cn(
                  'flex items-center justify-center w-5 h-5 rounded-full shrink-0',
                  isCurrent ? 'bg-violet-500 text-white' : 'bg-zinc-200 text-zinc-500'
                )}
              >
                {isCurrent ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Circle className="h-2 w-2" />
                )}
              </div>

              {/* Step info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      'font-medium truncate',
                      isCurrent ? 'text-violet-700' : 'text-zinc-600'
                    )}
                  >
                    {step.label || getStepTypeLabel(step.step_type)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                  <span>{getStepTypeLabel(step.step_type)}</span>
                  {step.delay_days > 0 && (
                    <>
                      <span>·</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">
                            +{step.delay_days}d delay
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p className="text-xs">
                            Will wait {step.delay_days} day{step.delay_days !== 1 ? 's' : ''} before executing
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>

              {/* Step type icon */}
              <div className="text-zinc-400 shrink-0">
                {getStepIcon(step.step_type)}
              </div>
            </div>
          );
        })}

        {hasMore && !showAll && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="w-full text-center text-[11px] text-violet-600 hover:text-violet-700 py-1"
          >
            Show {upcomingSteps.length - maxItems} more steps
          </button>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ExecutionPlan;
